/**
 * Role HTTP Client - Infrastructure Adapter
 *
 * Implements IRoleApiPort using HTTP API calls.
 * Handles all API communication and data transformation.
 *
 * @module domains/personnel/infrastructure/api/RoleHttpClient
 */

import type { AxiosInstance } from 'axios';
import type { IRoleApiPort } from '@personnel/application/ports/IRoleApiPort';
import type { Role } from '@personnel/domain/entities/Role';
import type { RoleInputDto } from '@personnel/application/dtos';
import type { RolesListApiResponse, RoleApiResponse } from './types';

import { PERSONNEL_API_ENDPOINTS } from './endpoints';
import {
  mapApiRolesListToDomain,
  mapApiRoleToDomain,
  mapRoleInputDtoToApiRequest,
} from '@personnel/infrastructure/mappers/roleMapper';

/**
 * Creates an HTTP-based implementation of IRoleApiPort
 *
 * This adapter:
 * - Calls the HTTP API
 * - Transforms API responses to Domain entities
 * - Transforms Domain DTOs to API requests
 *
 * @param props - Configuration with HTTP client
 * @returns IRoleApiPort implementation
 *
 * @example
 * const apiPort = createRoleHttpClient({ httpClient: connectApi });
 * const roles = await apiPort.getRoles(); // Returns Role[] entities
 */
export const createRoleHttpClient = (props: { httpClient: AxiosInstance }): IRoleApiPort => {
  const { httpClient } = props;

  const getRoles = async (): Promise<Role[]> => {
    const response = await httpClient.get<RolesListApiResponse>(PERSONNEL_API_ENDPOINTS.ROLES.LIST);
    // ✅ Transform API response → Domain entities
    return mapApiRolesListToDomain(response.data);
  };

  const createRole = async (data: RoleInputDto): Promise<Role> => {
    // ✅ Transform Domain DTO → API request
    const apiRequest = mapRoleInputDtoToApiRequest(data);

    const response = await httpClient.post<RoleApiResponse>(PERSONNEL_API_ENDPOINTS.ROLES.CREATE, apiRequest);

    // ✅ Transform API response → Domain entity
    return mapApiRoleToDomain(response.data.data);
  };

  const updateRole = async (uuid: string, data: RoleInputDto): Promise<Role> => {
    // ✅ Transform Domain DTO → API request
    const apiRequest = mapRoleInputDtoToApiRequest(data);

    const endpoint = PERSONNEL_API_ENDPOINTS.ROLES.UPDATE.replace('{roleUuid}', uuid);
    const response = await httpClient.patch<RoleApiResponse>(endpoint, apiRequest);

    // ✅ Transform API response → Domain entity
    return mapApiRoleToDomain(response.data.data);
  };

  const deleteRole = async (uuid: string): Promise<void> => {
    const endpoint = PERSONNEL_API_ENDPOINTS.ROLES.DELETE.replace('{roleUuid}', uuid);
    // DELETE returns 204 No Content - no response body
    await httpClient.delete(endpoint);
  };

  const getRoleByUuid = async (uuid: string): Promise<Role> => {
    const endpoint = PERSONNEL_API_ENDPOINTS.ROLES.GET.replace('{roleUuid}', uuid);
    const response = await httpClient.get<RoleApiResponse>(endpoint);

    // ✅ Transform API response → Domain entity
    return mapApiRoleToDomain(response.data.data);
  };

  // ✅ Returns IRoleApiPort implementation
  return {
    getRoles,
    createRole,
    updateRole,
    deleteRole,
    getRoleByUuid,
  };
};
